import { sideLandmarks } from '@erectus/shared';
import type { Landmark, Side } from '@erectus/shared';
import { signedAngleFromVertical, sub, threePointAngle, toPlane, type Point } from './geometry.js';
import { anteriorSign, craniovertebralAngle, type MetricContext } from './index.js';

/**
 * Standing side-on alignment — the plumb-line idea measured from one photo:
 * how far the ear, shoulder, hip and knee sit in front of (or behind) a
 * vertical line through the ankle. Pure functions over one frame, like the
 * seated metrics; the check takes the median over ~10 s.
 *
 * Offsets are a percentage of the ankle-to-ear height, so they do not depend on
 * how far away the camera is. Positive = in front of the ankle line.
 */
export interface StandingAlignment {
  /** Ear ahead of the ankle line, % of ear–ankle height. */
  earOffsetPct: number;
  shoulderOffsetPct: number;
  hipOffsetPct: number;
  kneeOffsetPct: number;
  /** Ankle→ear line against vertical, degrees; positive = leaning forward. */
  bodyLineDeg: number;
  /** Ankle→shoulder line against vertical, degrees; positive = forward. */
  shoulderLineDeg: number;
  /** Estimated CVA while standing (the seated CVA, standing). */
  cva: number | null;
  /** Hip–knee–ankle angle; ~180° when the knee is straight. */
  kneeAngle: number;
}

export function standingAlignment(landmarks: readonly Landmark[], side: Side, aspect: number): StandingAlignment | null {
  const idx = sideLandmarks(side);
  const pt = (i: number): Point | null => {
    const lm = landmarks[i];
    return lm ? toPlane(lm, aspect) : null;
  };
  const ear = pt(idx.ear);
  const shoulder = pt(idx.shoulder);
  const hip = pt(idx.hip);
  const knee = pt(idx.knee);
  const ankle = pt(idx.ankle);
  if (!ear || !shoulder || !hip || !knee || !ankle) return null;

  const height = ankle.y - ear.y;
  if (height <= 1e-6) return null;
  const ctx: MetricContext = { landmarks, side, aspect, hipAvailable: true, kneeAvailable: true };
  const sign = anteriorSign(ctx);
  const offset = (p: Point): number => (((p.x - ankle.x) * sign) / height) * 100;

  return {
    earOffsetPct: offset(ear),
    shoulderOffsetPct: offset(shoulder),
    hipOffsetPct: offset(hip),
    kneeOffsetPct: offset(knee),
    bodyLineDeg: signedAngleFromVertical(sub(ear, ankle), sign),
    shoulderLineDeg: signedAngleFromVertical(sub(shoulder, ankle), sign),
    cva: craniovertebralAngle(ctx),
    kneeAngle: threePointAngle(hip, knee, ankle),
  };
}

/** Median of each field over the samples of one check; null without samples. */
export function medianAlignment(samples: readonly StandingAlignment[]): StandingAlignment | null {
  if (samples.length === 0) return null;
  const median = (pick: (s: StandingAlignment) => number | null): number | null => {
    const values = samples
      .map(pick)
      .filter((v): v is number => v !== null && Number.isFinite(v))
      .sort((a, b) => a - b);
    return values.length === 0 ? null : values[Math.floor(values.length / 2)]!;
  };
  return {
    earOffsetPct: median((s) => s.earOffsetPct)!,
    shoulderOffsetPct: median((s) => s.shoulderOffsetPct)!,
    hipOffsetPct: median((s) => s.hipOffsetPct)!,
    kneeOffsetPct: median((s) => s.kneeOffsetPct)!,
    bodyLineDeg: median((s) => s.bodyLineDeg)!,
    shoulderLineDeg: median((s) => s.shoulderLineDeg)!,
    cva: median((s) => s.cva),
    kneeAngle: median((s) => s.kneeAngle)!,
  };
}
